import { Button, Form, Grid, Select } from 'antd';
import { ContentFilters } from 'components';
import { StandardItemsWrapper } from 'components/content-filters/content-filters.styles';
import { useGetExpensesDropDownFlag } from 'features/expenses-report/use-cases/get-dropdownReport';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpensesFilterFormValues } from './types';

const { useBreakpoint } = Grid;

type FieldsFilterFormProps = {
  formId: string;
  handleFilter: (values: ExpensesFilterFormValues) => void;
  initialValues: ExpensesFilterFormValues;
  closeModal?: () => void;
};

export const ExpensesFilterForm = ({
  formId,
  handleFilter,
  initialValues,
  closeModal,
}: FieldsFilterFormProps) => {
  const [validateTrigger, setValidateTrigger] = useState(['onSubmit']);
  const [form] = Form.useForm<ExpensesFilterFormValues>();
  const { t } = useTranslation();
  const screens = useBreakpoint();

  const [params, setParams] = useState<{
    Flag: string;
    facility_cascade_id?: string;
    field_cascade_id?: string;
    SearchKey?: string;
  }>({
    Flag: 'Normal',
  });

  const { data, isFetching } = useGetExpensesDropDownFlag(params as any, {
    enabled: !!params.Flag,
    keepPreviousData: true,
  });

  const [facilities, setFacilities] = useState([]);
  const [fields, setFields] = useState([]);
  const [sportnames, setSportnames] = useState([]);

  useEffect(() => {
    if (data?.items) {
      if (params.field_cascade_id) {
        setSportnames(data.items);
      } else if (params.facility_cascade_id) {
        setFields(data.items);
      } else {
        setFacilities(data.items);
      }
    }
  }, [data]);

  const facilitySelected = Form.useWatch('FacilityId', form);
  const fieldSelected = Form.useWatch('FieldId', form);

  const onReset = () => {
    form.resetFields();
    setFacilities([]);
    setFields([]);
    setSportnames([]);
    setParams({ Flag: 'Normal' });
  };

  const handleOnFinish = (values: ExpensesFilterFormValues) => {
    handleFilter(values);
    closeModal?.();
  };

  const handleFacilityChange = (facilityId: string) => {
    form.setFieldsValue({ FieldId: undefined, SportsId: undefined });

    setParams({
      Flag: 'Normal',
      facility_cascade_id: facilityId,
      SearchKey: undefined,
    });
  };
  const handleFacilitySearch = (input: string) => {
    setParams({
      Flag: 'Normal',
      SearchKey: input,
    });
  };

  const handleFieldChange = (fieldId: string) => {
    form.setFieldsValue({ SportsId: undefined });
    setParams((prev) => ({
      ...prev,
      field_cascade_id: fieldId,
      SearchKey: undefined,
    }));
  };

  const handleFieldSearch = (input: string) => {
    setParams((prev) => ({
      ...prev,
      SearchKey: input,
    }));
  };

  const handleSportnameSearch = (input: string) => {
    setParams((prev) => ({
      ...prev,
      SearchKey: input,
    }));
  };

  return (
    <Form<ExpensesFilterFormValues>
      id={formId}
      name={formId}
      form={form}
      layout='vertical'
      onFinish={handleOnFinish}
      initialValues={initialValues}
      onFinishFailed={() => {
        setValidateTrigger(['onChange']);
      }}
      validateTrigger={validateTrigger}
    >
      <ContentFilters.FormItemsWrapper>
        <StandardItemsWrapper>
          <Form.Item label={'Facility'} name='FacilityId'>
            <Select
              showSearch
              allowClear
              placeholder='Select Facility'
              loading={isFetching}
              onChange={handleFacilityChange}
              onSearch={handleFacilitySearch}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp='label'
              options={facilities.map((f: any) => ({
                label: f.Text,
                value: f.Id,
              }))}
            />
          </Form.Item>

          <Form.Item label='Field' name='FieldId'>
            <Select
              showSearch
              allowClear
              placeholder='Select Field'
              disabled={!facilitySelected || !fields.length}
              loading={isFetching}
              onChange={handleFieldChange}
              onSearch={handleFieldSearch}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp='label'
              options={fields.map((f: any) => ({
                label: f.Text,
                value: f.Id,
              }))}
            />
          </Form.Item>

          <Form.Item label='Sport' name='SportsId'>
            <Select
              showSearch
              placeholder='Select Sportname'
              disabled={
                !facilitySelected || !fieldSelected || !sportnames.length
              }
              loading={isFetching}
              onSearch={handleSportnameSearch}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp='label'
              options={sportnames.map((s: any) => ({
                label: s.Text,
                value: s.Id,
              }))}
            />
          </Form.Item>
        </StandardItemsWrapper>
      </ContentFilters.FormItemsWrapper>

      {screens.lg && (
        <ContentFilters.ButtonsWrapper>
          <Button type='primary' htmlType='submit'>
            {t('fields.buttons.filter', { count: 1 })}
          </Button>
          <Button onClick={onReset}>{t('fields.buttons.reset')}</Button>
        </ContentFilters.ButtonsWrapper>
      )}
    </Form>
  );
};
